import {Component} from "react";
import {Draw, Snap} from 'ol/interaction';
import {addKeyboardListener, isPermitted, removeKeyboardListeners} from "../../../utils/listeners";
import {CircularProgress, IconButton, Tooltip} from "@mui/material";
import {isROIFeature} from "../utils/annotations_app_utils";
import {store} from "../../../helper/store";
import {notifyWarning} from "../utils/display.notification";
import {addAnnotation} from "../../../redux_actions/maps.state.action";
import {AnnotationsConstants, DrawToolPixelTolerance} from "../../../utils/const";
import {getCenter} from "ol/extent";
import {getFormattedArea, getLoggedInUserId, getUperpixel, isTrue} from "../../../utils/utils";
import LineString from "ol/geom/LineString";
import cookie from "react-cookies";

class BaseDrawTool extends Component {

	componentDidMount() {
		this.init();

		const mapElement = this.props.slidemap.getViewport();
		mapElement.addEventListener('contextmenu', (event) => event.preventDefault());
		addKeyboardListener(document, this.keyDown);
	}

	componentWillUnmount() {
		removeKeyboardListeners(document, this.keyDown);
	}

	componentDidUpdate(prevProps, prevState, snapshot) {
		if (prevProps.selected !== this.props.selected) {
			this.setActive();
		}
		if (prevProps.slidemap !== this.props.slidemap) {
			this.addInteractions();
		}
	}

	init() {
		this.draw = new Draw({
			id: this.id,
			source: this.props.layer.getSource(),
			type: this.type,
			maxPoints: this.maxPoints,
			freehand: this.freehand,
			geometryFunction: this.geometryFunction,
			condition: this.condition,
		});
		this.draw.on("drawstart", this.onDrawStart);
		this.draw.on("drawend", this.onDrawEnd);

		this.snap = new Snap({
			source: this.props.layer.getSource(),
			pixelTolerance: DrawToolPixelTolerance.LOW_PIXEL_TOLERANCE,
			edge: true,
			vertex: true,
		});

		this.addInteractions();
		this.setActive();
	}

	disableDrawAnnotation = () => {
		this.isActive() && this.toggle();
		this.distanceOverlay?.close();
		this.angleOverlay?.close();
	}

	condition = (event) => {
		switch (event.originalEvent.button) {
			case 0:
				return true;
			case 2:
				this.disableDrawAnnotation();
				return false;
			default:
				this.draw.setActive(false);
				this.draw.setActive(true);
				return false;
		}
	}

	addInteractions = () => {
		this.props.slidemap.addInteraction(this.draw);
		this.props.slidemap.addInteraction(this.snap);
	}

	keyDown = (event) => {
		switch (event.key) {
			case this.shortcutKey:
				if (!isPermitted(event)) return;
				this.toggle();
				break;
			case "Escape":
				this.disableDrawAnnotation()
				break;
			default:
				break;
		}
	}

	addFeatureAnnotation = (feature) => {
		let isROITool = this instanceof (this.props.layer.getProperties().roiTool || Number);
		let roi = this.props.layer.getSource().getFeatures().find(feature => isROIFeature(feature));
		let geometry = feature.getGeometry();
		let center = getCenter(geometry.getExtent());
		if (!isROITool && roi && !roi.getGeometry().intersectsCoordinate(center)) {
			return notifyWarning("roi", "The annotation is not in the region of the interest");
		}
		const width = {width: this.props.selectedAnnoWidth}
		store.dispatch(addAnnotation(this.props.activeMapId, {
			area: this.getArea(geometry) * Math.pow(this.props.slide.uperpixel, 2),
			perimeter: this.getPerimeter(geometry) * this.props.slide.uperpixel,
			color: isROITool ? AnnotationsConstants.ROI_COLOR : this.props.selectedDrawColor,
			new_bounds: JSON.stringify(this.getCoordinates(geometry)),
			slide: this.props.slide.id,
			creator: getLoggedInUserId(),
			shape: this.shape,
			tool_type: this.id,
			center: getCenter(geometry.getExtent()).toString(),
			anno_drawer: this.props.layer.values_.drawer,
			created_z_level: this.props.slidemap.getView().getZoom(),
			title: isROITool ? AnnotationsConstants.ROI_TITLE : this.getTitle(feature),
			meta: width,
		}));
	}

	addLabelAnnotation = (feature) => {
		let geometry = feature.getGeometry();
		store.dispatch(addAnnotation(this.props.activeMapId, {
			area: geometry.getArea() * Math.pow(this.props.slide.uperpixel, 2),
			perimeter: new LineString(geometry.getLinearRing(0).getCoordinates()).getLength() * this.props.slide.uperpixel,
			color: this.props.selectedDrawColor,
			new_bounds: JSON.stringify(geometry.getCoordinates()),
			slide: this.props.slide.id,
			creator: getLoggedInUserId(),
			shape: AnnotationsConstants.POLYGON,
			tool_type: AnnotationsConstants.TOOL_TYPE.LABEL,
			center: getCenter(geometry.getExtent()).toString(),
			anno_drawer: this.props.layer.values_.drawer,
			created_z_level: this.props.slidemap.getView().getZoom(),
			title: 'Label',
		}));
	}

	onDrawEnd = (event) => {
		this.addFeatureAnnotation(event.feature);
	}

	getGeometricParams = (geometry) => ({
		area: geometry.getArea() * Math.pow(getUperpixel(), 2),
		perimeter: new LineString(geometry.getLinearRing(0).getCoordinates()).getLength() * getUperpixel(),
		coordinates: geometry.getCoordinates(),
	});

	getArea = (geometry) => geometry.getArea();

	getPerimeter = (geometry) => new LineString(geometry.getLinearRing(0).getCoordinates()).getLength();

	getCoordinates = (geometry) => geometry.getCoordinates();

	getTitle = (feature) => {
		let suffix = cookie.loadAll().superuser === 'true' ? 'A ' : 'R ';
		return suffix + `${getFormattedArea(this.getGeometricParams(feature.getGeometry()).area)}`;
	};

	isEnable = () => this.props.layer.values_.tools.some(tool => tool.name === this.constructor.name);
	toggle = () => this.props.onSelect(this);

	isActive = () => this.props.selected === this;

	setActive = () => {
		this.draw.setActive(this.isActive());
		this.snap.setActive(this.isActive());
	}

	render = () => {
		return <Tooltip title={this.name} placement={"bottom"}>
			<IconButton id={this.id} key={this.id} onClick={this.toggle} disabled={!this.isEnable()}
						 color={this.isActive() ? "secondary" : "text"} className={this.className} sx={{minWidth: 40}}>
				{isTrue(this.props.loading) ? <CircularProgress color={"secondary"} size={22}/> : this.icon}
			</IconButton>
		</Tooltip>
	}
}

export default BaseDrawTool;
